import { ProspectActionKind, ProspectStatus } from "@prisma/client";
import { ReactNode } from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconCheck from "@/app/(components)/generics/Icons/IconCheck";
import IconClose from "@/app/(components)/generics/Icons/IconClose";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { ProspectActionTypeItem } from "@/app/types/prospect/ProspectAction";
import {
  PROSPECT_ACTION_FILTER_ALL,
  ProspectActionFilter,
} from "@/app/types/prospect/ProspectActionFilter";
import { ProspectListTab } from "@/app/types/prospect/ProspectListTab";
import { ProspectTableRow } from "@/app/types/prospect/ProspectTableRow";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import ProspectFilterMenu from "./ProspectFilterMenu";
import { PROSPECT_LIST_PLACEHOLDER_ROWS } from "./ProspectList.logic";
import styles from "./ProspectList.module.css";
import ProspectRow from "./ProspectRow";

type Props = {
  lang: string;
  prospects?: ProspectTableRow[];
  count?: number;
  titleKey?: TKey;
  emptyTextKey?: TKey;
  emptyNode?: ReactNode;
  onRowClick?: (prospectId: number, fullName: string) => void;
  onQualify?: (prospectId: number, previous: ProspectStatus, scoreFit: number) => void;
  onReject?: (prospectId: number, previous: ProspectStatus) => void;
  showStatus?: boolean;
  isConnected?: boolean;
  isPending?: boolean;
  isLoading?: boolean;
  onAddProspect?: () => void;
  tab?: ProspectListTab;
  onTabChange?: (tab: ProspectListTab) => void;
  actionTypes?: ProspectActionTypeItem[];
  actionFilter?: ProspectActionFilter;
  onActionFilterChange?: (filter: ProspectActionFilter) => void;
  onSelectAction?: (
    prospectId: number,
    kind: ProspectActionKind,
    type?: ProspectActionTypeItem,
  ) => void;
  onCreateActionType?: (prospectId: number) => void;
  onOpenHistory?: (prospectId: number, fullName: string) => void;
};

export default function ProspectList(props: Props) {
  const T = useT();
  const {
    lang,
    prospects,
    count,
    titleKey = TKey.PROSPECT_SEARCH_LIST_TITLE,
    emptyTextKey,
    emptyNode,
    onRowClick,
    onQualify,
    onReject,
    showStatus = false,
    isConnected = false,
    isPending = false,
    isLoading = false,
    onAddProspect,
    tab = ProspectListTab.ALL,
    onTabChange,
    actionTypes = [],
    actionFilter = PROSPECT_ACTION_FILTER_ALL,
    onActionFilterChange,
    onSelectAction,
    onCreateActionType,
    onOpenHistory,
  } = props;

  const hasActions = !!(onQualify || onReject || onSelectAction);
  const hasActionsColumn = hasActions || showStatus || !!onTabChange;
  const hasProspects = prospects !== undefined;
  const isEmpty = hasProspects && prospects.length === 0;

  return (
    <div className={UStyles.classNames(styles.card, hasActionsColumn && styles.withActions)}>
      <div className={styles.header}>
        <h2 className={styles.title}>
          {T(titleKey)}
          {hasProspects ? ` (${count ?? prospects.length})` : ""}
        </h2>

        <div className={styles.headerActions}>
          {onAddProspect && (
            <Button
              text={T(TKey.PROSPECT_SEARCH_ADD_MANUAL_CTA)}
              type={ButtonType.OUTLINE}
              isLink={false}
              onClick={onAddProspect}
              min={true}
            />
          )}
        </div>
      </div>

      <div className={styles.scrollWrapper}>
        <div className={styles.tableInner}>
          <div className={styles.tableHeader}>
            <span>{T(TKey.PROSPECT_SEARCH_COL_PROFILE)}</span>
            <span>{T(TKey.PROSPECT_SEARCH_COL_SIGNALS)}</span>
            <span className={styles.scoreHeader}>{T(TKey.PROSPECT_SEARCH_COL_SCORE)}</span>
            {hasActionsColumn && (
              <span className={styles.filterCell}>
                {onTabChange && (
                  <ProspectFilterMenu
                    tab={tab}
                    onTabChange={onTabChange}
                    actionTypes={actionTypes}
                    actionFilter={actionFilter}
                    onActionFilterChange={onActionFilterChange}
                  />
                )}
              </span>
            )}
          </div>

          {isLoading ? (
            <div className={styles.empty}>
              <PointsLoader
                centered
                color="var(--color-green-3)"
                delay={Limits.PROSPECT_SEARCH_LOADER_DELAY}
              />
            </div>
          ) : isEmpty && (emptyNode || emptyTextKey) ? (
            <div className={styles.empty}>
              <p className={styles.emptyText}>{emptyNode ?? (emptyTextKey && T(emptyTextKey))}</p>
            </div>
          ) : isEmpty || (!hasProspects && isConnected) ? (
            <div className={styles.empty}>
              <h3 className={styles.emptyTitle}>
                {T(
                  isPending
                    ? TKey.PROSPECT_SEARCH_EMPTY_PENDING_TITLE
                    : TKey.PROSPECT_SEARCH_EMPTY_TITLE,
                )}
              </h3>
              <p className={styles.emptyText}>
                {T(
                  isPending
                    ? TKey.PROSPECT_SEARCH_EMPTY_PENDING_TEXT
                    : TKey.PROSPECT_SEARCH_EMPTY_TEXT,
                )}
              </p>
            </div>
          ) : hasProspects ? (
            <div className={styles.list}>
              {prospects.map((prospect) => (
                <ProspectRow
                  key={prospect.id}
                  prospect={prospect}
                  lang={lang}
                  onClick={onRowClick}
                  onQualify={onQualify}
                  onReject={onReject}
                  showStatus={showStatus}
                  actionTypes={actionTypes}
                  onSelectAction={onSelectAction}
                  onCreateActionType={onCreateActionType}
                  onOpenHistory={onOpenHistory}
                />
              ))}
            </div>
          ) : (
            <div className={UStyles.classNames(styles.list, styles.placeholder)}>
              {PROSPECT_LIST_PLACEHOLDER_ROWS.map((row, index) => (
                <div key={index} className={styles.row}>
                  <div className={styles.prospectCell}>
                    <span className={styles.avatarSkeleton} />
                    <div className={styles.prospectMeta}>
                      <span className={styles.bar} style={{ width: row.name }} />
                      <span className={styles.barSm} style={{ width: row.role }} />
                    </div>
                  </div>

                  <div className={styles.signalsCell}>
                    {row.signals.map((width, signalIndex) => (
                      <span key={signalIndex} className={styles.bar} style={{ width }} />
                    ))}
                  </div>

                  <div className={styles.scoreCell}>
                    <span className={styles.scoreSkeleton} style={{ width: row.score }} />
                  </div>

                  {hasActionsColumn && (
                    <div className={styles.actionsCell}>
                      {onReject && (
                        <span className={UStyles.classNames(styles.actionBtn, styles.actionReject)}>
                          <IconClose />
                        </span>
                      )}
                      {onQualify && (
                        <span
                          className={UStyles.classNames(styles.actionBtn, styles.actionQualify)}
                        >
                          <IconCheck color="currentColor" />
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
